import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, CheckCircle, MapPin, XCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { track } from "@/lib/track";

const NAVY = "#182848";
const ORANGE = "#EE7623";
const SLATE = "#4A5A72";

// Mirrors routes/service_area.py — used only if the API is unreachable.
const MD_RANGE = [20601, 21930];
const MD_FEDERAL = ["20331", "20335", "20395", "20588", "20597"];
const COPY = {
  core: "Yes — you're in our core service area. We work your neighborhood every week, so we can usually get to you fast.",
  maryland: "Yes — we serve your area. Scheduling takes a little more lead time outside our core ring, but you get the same crews and the same prices.",
  outside: "We're Maryland only right now — sorry we can't help with this one.",
};

function classifyLocal(zip, coreZips) {
  if (coreZips.includes(zip)) return "core";
  const n = Number(zip);
  return (n >= MD_RANGE[0] && n <= MD_RANGE[1]) || MD_FEDERAL.includes(zip) ? "maryland" : "outside";
}

export const quoteHrefForZip = (zip) => `/?zip=${encodeURIComponent(zip)}#quote`;

export const ZipChecker = ({ onResult }) => {
  const [zip, setZip] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const coreRef = useRef([]);

  useEffect(() => {
    api.get("/service-area/config").then(({ data }) => { coreRef.current = data.core_zips || []; }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const z = zip.trim();
    if (!/^\d{5}$/.test(z)) { setError("Enter a 5-digit ZIP code."); setRes(null); if (onResult) onResult(null); return; }
    setError(""); setBusy(true);
    track("zip_checked", { zip: z });
    let out;
    try {
      const { data } = await api.post("/service-area/check", { zip: z, source: "service-area" });
      out = data;
    } catch {
      const result = classifyLocal(z, coreRef.current);
      out = { zip: z, result, serviceable: result !== "outside", message: COPY[result] };
    }
    setRes(out); setBusy(false);
    if (onResult) onResult(out);
    track(out.serviceable ? "zip_serviceable" : "zip_not_serviceable", { zip: z, tier: out.result });
  };

  const onChange = (e) => {
    setZip(e.target.value.replace(/\D/g, "").slice(0, 5));
    if (error) setError("");
  };

  return (
    <div className="mt-8 max-w-[560px]" data-testid="zip-checker">
      <form onSubmit={submit} noValidate className="flex flex-col gap-3 sm:flex-row">
        <div className="flex-1">
          <label htmlFor="sa-zip" className="font-mono-label mb-2 block text-[10px] tracking-[0.24em]" style={{ color: ORANGE }}>
            Your ZIP code
          </label>
          <input
            id="sa-zip"
            data-testid="zip-input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="postal-code"
            maxLength={5}
            placeholder="Enter your ZIP code"
            value={zip}
            onChange={onChange}
            aria-invalid={!!error}
            aria-describedby={error ? "sa-zip-error" : undefined}
            className="h-14 w-full rounded-sm border-2 bg-white px-4 text-[18px] font-bold tracking-[0.08em] outline-none transition-colors focus:border-[#EE7623]"
            style={{ borderColor: error ? "#B42318" : NAVY, color: NAVY }}
          />
        </div>
        <button
          type="submit"
          data-testid="zip-submit"
          disabled={busy}
          className="inline-flex h-14 items-center justify-center gap-2 rounded-sm px-7 text-[15px] font-black text-white transition-colors disabled:opacity-70 sm:mt-[26px]"
          style={{ backgroundColor: ORANGE }}
        >
          {busy ? "Checking…" : "Check My Area"}
        </button>
      </form>

      <div aria-live="polite" role="status" className="mt-3" data-testid="zip-result-region">
        {error && (
          <p id="sa-zip-error" data-testid="zip-error" className="text-[14px] font-bold" style={{ color: "#B42318" }}>
            {error}
          </p>
        )}
        {res && (
          <div
            data-testid={`zip-result-${res.result}`}
            className="rounded-sm border-l-4 bg-white/80 p-4"
            style={{ borderColor: res.serviceable ? ORANGE : SLATE }}
          >
            <div className="flex items-start gap-3">
              {res.serviceable ? <CheckCircle size={22} weight="fill" style={{ color: ORANGE }} className="mt-0.5 flex-none" /> : <XCircle size={22} weight="fill" style={{ color: SLATE }} className="mt-0.5 flex-none" />}
              <div>
                <p className="text-[15px] font-semibold leading-snug" style={{ color: NAVY }} data-testid="zip-result-message">
                  {res.message}
                </p>
                {res.group && (
                  <p className="mt-1 inline-flex items-center gap-1 text-[12px]" style={{ color: SLATE }} data-testid="zip-result-group">
                    <MapPin size={12} weight="fill" /> {res.group} · {res.zip}
                  </p>
                )}
                {res.serviceable && (
                  <a
                    href={quoteHrefForZip(res.zip)}
                    onClick={() => track("quote_click_from_checker", { zip: res.zip, tier: res.result })}
                    data-testid="zip-quote-cta"
                    className="mt-3 inline-flex h-12 items-center gap-2 rounded-sm px-5 text-[14px] font-black text-white"
                    style={{ backgroundColor: NAVY }}
                  >
                    Get My Free Quote <ArrowRight size={16} weight="bold" />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
